import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShieldAlt, faSave, faSync, faCheckSquare,
    faSquare, faDatabase, faLink
} from '@fortawesome/free-solid-svg-icons';
import { Spinner } from 'react-bootstrap';
import request from '../../../../../request';
import Notify from '../../../../compartidos/Notify';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/';

/**
 * ProfilePermissions — Permission matrix: Profile × Module.
 * Select a profile → see all modules → toggle CanView / CanEdit / CanDelete.
 * Bulk-saves the entire matrix on "Guardar".
 */
const ProfilePermissions = () => {
    const [profiles,  setProfiles]  = useState([]);
    const [modules,   setModules]   = useState([]);
    const [selProfile, setSelProfile] = useState('');
    const [matrix,    setMatrix]    = useState({}); // { [ModuleId]: { CanView, CanEdit, CanDelete } }
    const [loading,   setLoading]   = useState(false);
    const [saving,    setSaving]    = useState(false);
    const [dirty,     setDirty]     = useState(false);

    useEffect(() => {
        request.Get(API_BASE + 'superadmin/profiles', {}, r => { if (r.success) setProfiles(r.data || []); });
        request.Get(API_BASE + 'superadmin/modules',  {}, r => { if (r.success) setModules(r.data  || []); });
    }, []);

    useEffect(() => {
        if (!selProfile) return;
        setLoading(true);
        setDirty(false);
        request.Get(API_BASE + `superadmin/profile-permissions/${selProfile}`, {}, r => {
            if (r.success) {
                // Build matrix from returned array
                const m = {};
                // Init all modules as NOT assigned
                modules.forEach(mod => {
                    m[mod.ModuleId] = { CanView: 0, CanEdit: 0, CanDelete: 0 };
                });
                // Overlay actual permissions
                (r.data || []).forEach(perm => {
                    m[perm.ModuleId] = {
                        CanView:   perm.CanView   ?? 1,
                        CanEdit:   perm.CanEdit   ?? 0,
                        CanDelete: perm.CanDelete ?? 0
                    };
                });
                setMatrix(m);
            } else {
                Notify({ type: 'fail', text: 'Error al cargar permisos' });
            }
            setLoading(false);
        }, 'json', () => setLoading(false));
    }, [selProfile]);  // eslint-disable-line

    const toggle = (moduleId, field) => {
        setMatrix(prev => ({
            ...prev,
            [moduleId]: {
                ...prev[moduleId],
                [field]: prev[moduleId]?.[field] ? 0 : 1
            }
        }));
        setDirty(true);
    };

    const selectAll = () => {
        const m = {};
        modules.forEach(mod => { m[mod.ModuleId] = { CanView: 1, CanEdit: 1, CanDelete: 1 }; });
        setMatrix(m);
        setDirty(true);
    };

    const clearAll = () => {
        const m = {};
        modules.forEach(mod => { m[mod.ModuleId] = { CanView: 0, CanEdit: 0, CanDelete: 0 }; });
        setMatrix(m);
        setDirty(true);
    };

    const handleSave = () => {
        setSaving(true);
        const permissions = Object.entries(matrix).map(([moduleId, perms]) => ({
            ModuleId: parseInt(moduleId), ...perms
        }));
        request.Post(API_BASE + 'superadmin/profile-permissions', {
            profileId: selProfile, permissions
        }, r => {
            if (r.success) {
                Notify({ type: 'success', text: 'Permisos guardados correctamente' });
                setDirty(false);
            } else {
                Notify({ type: 'fail', text: r.msg || 'Error al guardar permisos' });
            }
            setSaving(false);
        }, 'json', () => setSaving(false));
    };

    const PermCell = ({ moduleId, field, label }) => {
        const checked = matrix[moduleId]?.[field] === 1;
        return (
            <td style={{ textAlign: 'center' }}>
                <button
                    onClick={() => toggle(moduleId, field)}
                    style={{
                        background: 'none', border: 'none', cursor: 'pointer', padding: 4,
                        color: checked
                            ? field === 'CanView' ? '#a855f7' : field === 'CanEdit' ? '#f59e0b' : '#ef4444'
                            : 'var(--border-color)',
                        fontSize: '1.1rem',
                        transition: 'all 0.15s'
                    }}
                    title={`${label}: ${checked ? 'Quitar' : 'Dar'}`}
                >
                    <FontAwesomeIcon icon={checked ? faCheckSquare : faSquare} />
                </button>
            </td>
        );
    };

    return (
        <div>
            {/* Profile selector + actions */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div className="d-flex gap-3 align-items-center flex-wrap" style={{ flex: 1 }}>
                    <select
                        className="sa-input"
                        style={{ maxWidth: 280 }}
                        value={selProfile}
                        onChange={e => setSelProfile(e.target.value)}
                    >
                        <option value="">— Seleccioná un Perfil —</option>
                        {profiles.map(p => (
                            <option key={p.ProfileId} value={p.ProfileId}>{p.ProfileName}</option>
                        ))}
                    </select>
                    {selProfile && (
                        <>
                            <button className="sa-btn sa-btn-ghost sa-btn-sm" onClick={selectAll}>Marcar todo</button>
                            <button className="sa-btn sa-btn-danger sa-btn-sm" onClick={clearAll}>Limpiar todo</button>
                        </>
                    )}
                </div>
                {dirty && (
                    <button className="sa-btn sa-btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : <><FontAwesomeIcon icon={faSave} /> Guardar Permisos</>}
                    </button>
                )}
            </div>

            {!selProfile ? (
                <div className="sa-empty">
                    <FontAwesomeIcon icon={faShieldAlt} style={{ fontSize: '3rem', opacity: 0.1, display: 'block', margin: '0 auto 1rem' }} />
                    <p style={{ color: '#475569' }}>Seleccioná un perfil para gestionar sus permisos</p>
                </div>
            ) : loading ? (
                <div className="text-center py-5"><Spinner animation="grow" style={{ color: 'var(--sa-primary)' }} /></div>
            ) : (
                <div className="sa-panel">
                    {/* Legend */}
                    <div style={{ padding: '0.75rem 1.5rem', borderBottom: '1px solid var(--border-color)' }}>
                        <div className="d-flex gap-4 flex-wrap" style={{ fontSize: '0.75rem' }}>
                            <span style={{ color: '#a855f7', fontWeight: 700 }}>
                                <FontAwesomeIcon icon={faCheckSquare} className="me-1" /> Ver
                            </span>
                            <span style={{ color: '#f59e0b', fontWeight: 700 }}>
                                <FontAwesomeIcon icon={faCheckSquare} className="me-1" /> Editar
                            </span>
                            <span style={{ color: '#ef4444', fontWeight: 700 }}>
                                <FontAwesomeIcon icon={faCheckSquare} className="me-1" /> Eliminar
                            </span>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table className="sa-table">
                            <thead>
                                <tr>
                                    <th>Módulo</th>
                                    <th>Key</th>
                                    <th>Tipo</th>
                                    <th style={{ textAlign: 'center', color: '#a855f7' }}>Ver</th>
                                    <th style={{ textAlign: 'center', color: '#f59e0b' }}>Editar</th>
                                    <th style={{ textAlign: 'center', color: '#ef4444' }}>Eliminar</th>
                                </tr>
                            </thead>
                            <tbody>
                                {modules.length === 0 && (
                                    <tr><td colSpan={6} className="text-center py-4 text-muted">Sin módulos. Creá módulos primero.</td></tr>
                                )}
                                {modules.map(mod => (
                                    <tr key={mod.ModuleId}>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{
                                                    width: 28, height: 28, borderRadius: 7,
                                                    background: mod.ModuleActive ? 'rgba(168,85,247,0.1)' : '#f1f5f9',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    color: mod.ModuleActive ? 'var(--sa-primary)' : '#94a3b8', fontSize: '0.7rem'
                                                }}>
                                                    <FontAwesomeIcon icon={mod.ModuleType === 'DYNAMIC' ? faDatabase : faLink} />
                                                </div>
                                                <span style={{ fontWeight: 600, fontSize: '0.88rem', color: mod.ModuleActive ? 'var(--text-main)' : 'var(--text-dim)' }}>
                                                    {mod.ModuleLabel}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <code style={{ color: '#64748b', fontSize: '0.72rem' }}>{mod.ModuleKey}</code>
                                        </td>
                                        <td>
                                            <span className={`sa-badge ${mod.ModuleType === 'DYNAMIC' ? 'sa-badge-warning' : 'sa-badge-trial'}`} style={{ fontSize: '0.62rem' }}>
                                                {mod.ModuleType}
                                            </span>
                                        </td>
                                        <PermCell moduleId={mod.ModuleId} field="CanView"   label="Ver" />
                                        <PermCell moduleId={mod.ModuleId} field="CanEdit"   label="Editar" />
                                        <PermCell moduleId={mod.ModuleId} field="CanDelete" label="Eliminar" />
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {dirty && (
                <div style={{
                    position: 'fixed', bottom: '2rem', right: '2rem',
                    background: 'rgba(15,23,42,0.9)', backdropFilter: 'blur(12px)',
                    border: '1px solid rgba(168,85,247,0.3)', borderRadius: 14,
                    padding: '0.75rem 1.25rem', display: 'flex', alignItems: 'center', gap: '1rem',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.5)'
                }}>
                    <span style={{ fontSize: '0.82rem', color: '#f59e0b' }}>
                        ⚠ Tenés cambios sin guardar
                    </span>
                    <button className="sa-btn sa-btn-primary sa-btn-sm" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner size="sm" animation="border" /> : <><FontAwesomeIcon icon={faSave} /> Guardar</>}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ProfilePermissions;
